import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    this.closestSection = this.closest('.shopify-section, dialog');
    if (!this.closestSection) return;
    this.closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    this.closestSection.addEventListener('change', this.handleAddonSelectChange);
    this.syncInitialPrice();
  }

  disconnectedCallback() {
    if (!this.closestSection) return;
    this.closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    this.closestSection.removeEventListener('change', this.handleAddonSelectChange);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    if (event.detail.resource) {
      this.renderVariantPrice(event.detail.resource);
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }

    this.syncInitialPrice();
  };

  handleAddonSelectChange = (event) => {
    if (!(event.target instanceof HTMLSelectElement)) return;
    if (!event.target.matches('[data-addon-select]')) return;
    if (event.target.dataset.productId !== this.dataset.productId) return;

    this.syncInitialPrice();
  };

  syncInitialPrice() {
    const productId = this.dataset.productId;
    if (!productId) return;

    const variantPicker = this
      .closest('.shopify-section, dialog')
      ?.querySelector(`variant-picker[data-product-id="${productId}"]`);

    if (!(variantPicker instanceof HTMLElement)) return;

    const selectedVariantScript = variantPicker.querySelector('script[type="application/json"]:not([data-variant-data])');
    if (!(selectedVariantScript instanceof HTMLScriptElement) || !selectedVariantScript.textContent) return;

    try {
      const variant = JSON.parse(selectedVariantScript.textContent);
      if (variant?.id) {
        this.renderVariantPrice(variant);
      }
    } catch (error) {
      console.error('Unable to parse selected variant price data', error);
    }
  }

  renderVariantPrice(variant) {
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (!(currentPrice instanceof HTMLElement)) return;

    const priceElement = currentPrice.querySelector('.price');
    if (!(priceElement instanceof HTMLElement)) return;

    const addonPricing = this.getSelectedAddonPricing();
    const variantPrice = Number(variant.price || 0);
    priceElement.textContent = this.formatPrice(
      variantPrice + addonPricing.price,
      priceElement.textContent,
      currentPrice.dataset.priceTemplate
    );

    const compareAtPriceElement = currentPrice.querySelector('.compare-at-price');
    const variantCompareAtPrice = Number(variant.compare_at_price || 0);
    if (compareAtPriceElement instanceof HTMLElement && variantCompareAtPrice > variantPrice) {
      const addonCompareAtPrice = addonPricing.compareAtPrice > addonPricing.price ? addonPricing.compareAtPrice : addonPricing.price;
      compareAtPriceElement.textContent = this.formatPrice(
        variantCompareAtPrice + addonCompareAtPrice,
        compareAtPriceElement.textContent
      );
    }
  }

  getSelectedAddonPricing() {
    const productId = this.dataset.productId;
    if (!productId || !this.closestSection) return { price: 0, compareAtPrice: 0 };

    const addonSelect = this.closestSection.querySelector(`[data-addon-select][data-product-id="${productId}"]`);
    if (!(addonSelect instanceof HTMLSelectElement)) return { price: 0, compareAtPrice: 0 };

    const selectedOption = addonSelect.options[addonSelect.selectedIndex];
    if (!(selectedOption instanceof HTMLOptionElement) || !selectedOption.value) {
      return { price: 0, compareAtPrice: 0 };
    }

    const price = Number(selectedOption.dataset.addonPrice || 0);
    const compareAtPrice = Number(selectedOption.dataset.addonCompareAtPrice || 0);
    return { price, compareAtPrice };
  }

  formatPrice(priceInCents, currentText = '', priceTemplate = '') {
    const number = Number(priceInCents || 0) / 100;
    const locale = window.Shopify?.locale || document.documentElement.lang || 'en';
    const formattedNumber = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(number);

    const currentTextTrimmed = currentText.trim();
    const prefixMatch = currentTextTrimmed.match(/^[^\d]+/);
    const prefix = prefixMatch ? prefixMatch[0] : '';
    const currency = window.Shopify?.currency?.active || 'USD';
    const formattedPrice = prefix
      ? `${prefix}${formattedNumber}`
      : new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(number);

    if (priceTemplate) {
      return this.normalizeCurrencyText(priceTemplate.replace('__PRICE__', formattedPrice));
    }

    return this.normalizeCurrencyText(formattedPrice);
  }

  normalizeCurrencyText(text = '') {
    return text
      .replace(/\s+MYR$/, '')
      .replace(/^MYR\s*/, 'RM ')
      .replace(/MYR/g, 'RM')
      .replace(/\s+$/, '');
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
